import { Component, input, output, OnInit, Signal, computed, inject } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { TransactionAccount, TransactionCategory } from '../../../../../../_interfaces/transactions.interface';
import { TransactionsService } from '../../_service/transactions.service';
import { AccountingService } from '../../../_services/accounting/accounting.service';

@Component({
  selector: 'app-select-transaction-category',
  imports: [NgSelectModule, FormsModule, NgClass],
  templateUrl: './select-transaction-category.component.html',
})
export class SelectTransactionCategoryComponent implements OnInit {
  private readonly transactionService = inject(TransactionsService);
  readonly accountingService = inject(AccountingService);

  // Inputs
  selectedCategoryId = input<number | undefined>(undefined); // Initial selected category ID
  errorMessage = input<string>(''); // Error message to display (for is-invalid class)
  placeholder = input<string>('Search a category'); // Placeholder text
  clearable = input<boolean>(false); // Whether the select is clearable
  searchable = input<boolean>(true); // Whether the select is searchable
  showHiddenCategories = input<boolean>(true); // Whether to show invisible categories (disabled) or hide them completely
  readonly = input<boolean>(false);

  // Outputs
  categoryChange = output<{ categoryId?: number; account?: TransactionAccount; isVisible: boolean }>(); // Emits when category changes
  categorySyncError = output<boolean>(); // Emits true if the selected category has no account

  // Internal state - computed from service with processing applied
  categories: Signal<TransactionCategory[]> = computed(() => {
    // Get categories from a service
    const rawCategories = this.transactionService.categoryOptions();

    // Process categories: add account codes and set a disabled state
    let processedCategories = rawCategories.map((cat) => {
      // Create a copy to avoid mutating the original
      const processedCat = { ...cat };

      // Add account code to the category name for display
      const accCode = processedCat.account?.code ? ` (${processedCat.account?.code})` : '';
      processedCat.name = processedCat.name + accCode;

      // Disable invisible categories but keep them in a list to avoid blanks
      processedCat.disabled = !processedCat.is_visible;

      return processedCat;
    });

    // If showHiddenCategories is false, filter out invisible categories
    // BUT always include the selected category even if it's hidden (to avoid blank display)
    if (!this.showHiddenCategories()) {
      const selectedId = this.selectedCategoryId();
      processedCategories = processedCategories.filter((cat) => cat.is_visible || cat.id === selectedId);
    }

    return processedCategories;
  });

  selectedCategory: number | undefined;

  ngOnInit(): void {
    this.selectedCategory = this.selectedCategoryId();
  }

  findCat(selectedCatId: number) {
    return this.categories().find((cat) => cat.id === selectedCatId);
  }

  getSelectedCategoryAccount(selectedCatId?: number): TransactionAccount | undefined {
    if (!selectedCatId) return undefined;
    const cat = this.findCat(selectedCatId);
    if (!cat) return undefined;
    return cat.account;
  }

  getSelectedCategoryVisibility(selectedCatId?: number): boolean {
    if (!selectedCatId) return false;
    const cat = this.findCat(selectedCatId);
    if (!cat) return false;
    return cat.is_visible;
  }

  onCategoryChange(categoryId?: number) {
    this.selectedCategory = categoryId;

    // Emit the category change with both ID and account code
    this.categoryChange.emit({
      categoryId: categoryId,
      account: this.getSelectedCategoryAccount(categoryId),
      isVisible: this.getSelectedCategoryVisibility(categoryId),
    });

    // Check if the selected category has an account (sync check)
    const selectedCategoryObject = this.categories().find((cat) => cat.id === categoryId);
    const hasSyncError = !selectedCategoryObject?.account;

    // Emit sync error status
    this.categorySyncError.emit(hasSyncError);
  }
}
